import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { BehaviorSubject, Observable, Subject, defer, from, of } from 'rxjs';
import { catchError, map, switchMap, take, timeout } from 'rxjs/operators';
import { AppService } from './app.service';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { SignalRService } from './signalr.service';
import { isPlayerStateNullState } from './player-state-error';
import { RequestsType } from '../model/appConfig';
import { ErrorCode } from '../model/errorcode';
import { State, toStateArray } from '../model/state.model';
import { Platform } from '@ionic/angular';
import { User } from '../model/user.model';
import { IPlaylist } from '../model/playlist.interface';

@Injectable()
export class PlayerService {
    private interval: number = 0;
    public ready: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public state: State = (new State()).init();
    private prevTimeSong: number = 0;
    public isPlay: boolean = false;
    public commands: Object[] = [];
    private MAX_COMMAND_SEND: number = 10;
    public playlistLoaded: BehaviorSubject<boolean> = new BehaviorSubject(false);
    /** Emitted when a state packet includes `playlists` (command 99 / full sync). Used to resolve SignalR playlist fetch. */
    public readonly playlistsArrived = new Subject<IPlaylist[]>();
    private errCounter: number = 0;
    public songChanged: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public noResults: boolean = false;
    public deltaVolume: BehaviorSubject<number | null> = new BehaviorSubject(null);
    volumeChanged = new EventEmitter<{volume: number, force?: boolean}>();
    public platformPauseSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor(
        private router: Router,
        private authService: AuthService,
        private apiService: ApiService,
        private appService: AppService,
        private platform: Platform,
        private alertController: AlertController,
        public signalRService: SignalRService,
    ) {
        if (this.authService.authenticated) {
            this.start().catch(error => {
                console.error('Failed to start player service:', error);
            });
        }

        this.platformPauseSubject.subscribe((pauseValue: boolean) => {
            // on open app from background force to download all information in sync()
            if (pauseValue === false) {
                console.log('App resumed - resetting error counter and forcing sync');
                this.errCounter = 0; // Reset error counter on resume
                this.commands.push({ id: 99 });
            } else {
                console.log('App paused - maintaining background sync');
            }
        });

        this.signalRService.statesToProcess.subscribe((states: State[] | null) => {
            if (states !== null) {
                this.signalRService.statesToProcess.next(null);
                // "MyDjPlayerState_<id> is null" means the player has not reported any state yet
                // (offline / just started). The SignalR listener already drops such packets; the
                // HTTP (JSONP) transport used by the mobile builds delivers them here, and they
                // must not surface as an "Unknown error" popup. The server did answer, though,
                // so the connection is healthy - keep the error counter reset.
                const actionableStates = toStateArray(states).filter(state => !isPlayerStateNullState(state));
                this.errCounter = 0;
                if (actionableStates.length === 0) {
                    return;
                }
                actionableStates.forEach(state => { this.buildState(state); });
                this.ready.next(true);
            }
        });
    }

    public async start(): Promise<void> {
        this.errCounter = 0;
        const command: string = JSON.stringify({ "id": 99 });

        this.appService.showNotification("connecting...");
        try {
            const config = await this.appService.getConfig();
            if (config.requestsType === RequestsType.SignalR) {
                this.signalRService.sendMessage(this.authService.currentUser.name, command);
            } else {
                this.apiService.sendCommand(this.authService.currentUser, command)
                    .subscribe(states => {
                        this.signalRService.statesToProcess.next(states);
                    });
            }
        } catch (error) {
            console.error('Failed to get config during start:', error);
            // Fallback to HTTP mode
            this.apiService.sendCommand(this.authService.currentUser, command)
                .subscribe(states => {
                    this.signalRService.statesToProcess.next(states);
                });
        }

        this.interval = window.setInterval(() => {
            this.sync();
        }, 1000);
    }

    public stop(): void {
        clearInterval(this.interval);
        this.volumeChanged.emit({ volume: 0, force: true });
        this.state = (new State()).init();
        // Ensure the SignalR connection is closed so the next start connects with the current user
        try {
            this.signalRService.disconnect();
        } catch (err) {
            console.error('Error while disconnecting SignalR during stop:', err);
        }
    }

    private async sync(): Promise<void> {
        try {
            // 0 - получение минимума информации: текущего времени песни и некого кэш-тайма
            // 99 - получение максимума информации
            // если state еще не получен - дергаем сервер 99-й командой, если уже получен - то 0-й        
            let command: string = JSON.stringify({ id: this.state.currentSong.name === "" ? 99 : 0 });

            if (this.state.currentSong.name === "") {
                this.appService.showNotification("connecting...");
            }

            // remove extra 99 comands
            let count: number = this.commands.filter(c => (<any>c).id === 99).length;
            if (count > 1) {
                this.commands = this.commands.filter(c => (<any>c).id !== 99);
                this.commands.push({ id: 99 });
            }

            if (this.commands.length > 0) {
                if (this.commands.length > this.MAX_COMMAND_SEND) {
                    const firstCommands = this.commands.slice(0, this.MAX_COMMAND_SEND);
                    this.commands = this.commands.slice(this.MAX_COMMAND_SEND);
                    command = JSON.stringify(firstCommands);
                }
                else {
                    command = JSON.stringify(this.commands);
                    this.commands = [];
                }
            }
            
            try {
                const config = await this.appService.getConfig();
                if (config.requestsType === RequestsType.SignalR) {
                    this.signalRService.sendMessage(this.authService.currentUser.name, command);
                } else {
                    this.apiService.sendCommand(this.authService.currentUser, command)
                        .subscribe(states => {
                            this.signalRService.statesToProcess.next(states);
                        });
                }
            } catch (error) {
                console.error('Failed to get config during sync:', error);
                // Fallback to HTTP mode
                this.apiService.sendCommand(this.authService.currentUser, command)
                    .subscribe(states => {
                        this.signalRService.statesToProcess.next(states);
                    });
            }
        } catch (err) {
            console.error('Sync error:', err);
            this.appService.loading = "";
            
            // Only increment error counter if not in background
            if (!this.platformPauseSubject.value) {
                this.errCounter++;
                this.appService.showNotification("connection problem");
            } else {
                console.log('Background sync error (not counting):', err);
            }
            
            // Increased threshold to prevent premature logout
            if (this.errCounter > 50) {
                console.error('Too many errors, logging out');
                this.stop();
                this.authService.logout();
                this.router.navigateByUrl("/auth/login");
            }
        }
    }

    private async buildState(state: State): Promise<void> {
        try {
            if (state.errorCode && !isPlayerStateNullState(state)) {
                let errorHeader = "Error";
                let errorMessage: string;
                switch (state.errorCode) {
                    case ErrorCode.AuthFailed:
                        errorMessage = state.error;
                        break;
                    case ErrorCode.NoPermissionForCommand:
                        errorHeader = null;
                        errorMessage = `No permission for command ${state.error}`;
                        break;
                    default:
                        errorMessage = `Unknown error ${state.error}`;
                        break;
                }
                const alert = await this.alertController.create({
                    header: errorHeader,
                    message: errorMessage,
                    buttons: ['OK'],
                  });
                await alert.present();
            }

            this.prevTimeSong = this.state.currentTimeSong;

            for (const field in state) {
                if (field === "playlistSongs") {
                    this.state[field] = this.state[field].concat(state[field]);
                } else {
                    this.state[field] = state[field];
                }
                if (field === "volume") {
                    this.volumeChanged.emit({ volume: state[field] });
                }
            }
            if ((state as any).scheduler !== undefined) {
                this.state.scheduler = (state as any).scheduler;
            }

            if (state.playlists !== undefined && state.playlists !== null) {
                this.playlistsArrived.next(state.playlists);
            }

            this.isPlay = this.state.currentTimeSong !== this.prevTimeSong;

            if (state.searchResultsList) { // switch off preloader when got search result         
                this.appService.loading = "";
                this.noResults = !state.searchResultsList.length;
            }

            if (state.playlistSongs && state.playlistSongs.length) { // switch off preloader when got playlist songs
                this.appService.loading = "";
                this.playlistLoaded.next(true);
            }

            // if currentSong changed then request full info to update current playlist
            if (state.currentSong && state.volume === undefined) {
                this.commands.push({ id: 99 });
                this.songChanged.next(true);
            }
        } catch (err) {
            console.error('Error in buildState:', err);
            // Don't crash, just log the error
        }
    }

    /**
     * Loads playlists via the same command as full sync (id 99). HTTP: jsonp APIPhonePlayer; SignalR: hub message, then wait for playlists in state.
     */
    public requestPlaylistsList(user: User): Observable<IPlaylist[] | null> {
        const cmd = JSON.stringify({ id: 99 });
        const parsePlaylistsFromStates = (states: State[] | null | undefined): IPlaylist[] | null => {
            const withPlaylists = toStateArray(states).find(s => s && Array.isArray((s as any).playlists));
            const list = withPlaylists && Array.isArray((withPlaylists as any).playlists)
                ? (withPlaylists as any).playlists as IPlaylist[]
                : [];
            return list.length > 0 ? list : null;
        };

        return from(this.appService.getConfig()).pipe(
            switchMap(config => {
                if (config.requestsType === RequestsType.SignalR) {
                    // Подписка на playlistsArrived должна быть активна до sendMessage, иначе быстрый ответ теряется.
                    return defer(() => {
                        const stream = this.playlistsArrived.pipe(
                            take(1),
                            timeout(15000),
                            catchError(() => of(null as IPlaylist[] | null))
                        );
                        queueMicrotask(() => this.signalRService.sendMessage(user.name, cmd));
                        return stream;
                    });
                }
                return this.apiService.sendCommand(user, cmd).pipe(
                    map(states => parsePlaylistsFromStates(states)),
                    catchError(() => of(null))
                );
            })
        );
    }

    public setVolume(v: number): void {
        this.appService.showNotification("sending [volume]");
        let i: number = this.commands.findIndex(c => (<any>c).id === 8);

        if (i === -1) {
            this.commands.push({ id: 8, volume: v });
            this.commands.push({ id: 99 });
        } else {
            this.commands[i] = { id: 8, volume: v };
        }
    }

    public play(): void {
        this.appService.showNotification("sending [play]");
        this.commands.push({ id: 1 });
    }

    public pause(): void {
        this.appService.showNotification("sending [pause]");
        this.commands.push({ id: 2 });
    }

    public nextTrack(): void {
        this.appService.showNotification("sending [next]");
        this.commands.push({ id: 3 });
        this.commands.push({ id: 99 });
    }

    public happyBirthday(): void {
        this.appService.showNotification("sending [birthday]");
        this.commands.push({ id: 6 });
    }

    public setShuffle(value: boolean): void {
        this.appService.showNotification("sending [shuffle]");
        this.commands.push({ id: 7, shuffle: value ? "on" : "off" });
        this.commands.push({ id: 99 }); // после некоторых команд добавлена 99-я, чтобы принудительно получить статус от сервера       
    }

    public seek(seekTime: number): void {
        this.appService.showNotification("sending [seek]");
        this.commands.push({ id: 9, sec: seekTime });
    }

    public search(s: string): void {
        this.appService.showNotification("sending [search]");
        this.commands.push({ id: 14, searchString: s });
        this.commands.push({ id: 99 });
    }

    public remove(songID: number): void {
        this.appService.showNotification("sending [remove]");
        this.commands.push({ id: 15, songID: songID });
        this.commands.push({ id: 99 });
    }

    public deleteForever(songID: number): void {
        this.appService.showNotification("sending [delete forever]");
        this.commands.push({ id: 27, songID: songID });
        this.commands.push({ id: 99 });
    }

    public playNext(songID: number): void {
        this.appService.showNotification("sending [play next]");
        this.commands.push({ id: 16, songID: songID });
        this.commands.push({ id: 99 });
    }

    public playNow(songID: number): void {
        this.appService.showNotification("sending [play now]");
        this.commands.push({ id: 17, songID: songID });
        this.commands.push({ id: 99 });
    }

    public setPlaylist(playlistID: number): void {
        this.appService.showNotification("sending [set playlist]");
        this.commands.push({ id: 26, playlistID: playlistID });
        this.commands.push({ id: 99 });
    }

    public addPlaylist(playlistID: number): void {
        this.appService.showNotification("sending [add playlist]");
        this.commands.push({ id: 35, playlistID: playlistID });
        this.commands.push({ id: 99 });
    }

    public getPlaylistSongs(playlistID: number): void {
        this.appService.showNotification("sending [get playlist songs]");
        this.commands.push({ id: 13, playlistID: playlistID });
    }

    public playlistSongPlayNow(playlistID: number, songID: number): void {
        this.appService.showNotification("sending [playlist song - play now]");
        this.commands.push({ id: 22, playlistID: playlistID, songID: songID });
        this.commands.push({ id: 99 });
    }

    public playlistSongPlayNext(playlistID: number, songID: number): void {
        this.appService.showNotification("sending [playlist song - play next]");
        this.commands.push({ id: 21, playlistID: playlistID, songID: songID });
        this.commands.push({ id: 99 });
    }

    // в поисковом списке свои идентификаторы, поэтому для манипуляций с песнями из списка найденных существуют отдельные функции
    public playNextAfterSearch(songID: number): void {
        this.appService.showNotification("sending [play next]");
        this.commands.push({ id: 23, songID: songID });
        this.commands.push({ id: 99 });
    }

    public playNowAfterSearch(songID: number): void {
        this.appService.showNotification("sending [play now]");
        this.commands.push({ id: 24, songID: songID });
        this.commands.push({ id: 99 });
    }

    // additional Newyear functions
    public ny1(): void {
        this.appService.showNotification("sending [ny1]");
        this.commands.push({ id: 28 });
        this.commands.push({ id: 99 });
    }

    public ny2(): void {
        this.appService.showNotification("sending [ny2]");
        this.commands.push({ id: 29 });
        this.commands.push({ id: 99 });
    }

    public UpdateLikeStatusOfPlaylist(playlistId: number, likeType: number): void {
        this.appService.showNotification("sending like status");
        this.commands.push({ id: 33, "playlistID": playlistId, "likeType": likeType });
        this.commands.push({ id: 99 });
    }

    public UpdateLikeStatusOfSong(page: string, playlistId: number, songId: number, typeLike: number) {
        this.appService.showNotification("sending like status");
        this.commands.push({ id: 32, page: page, playlistID: playlistId, songID: songId, likeType: typeLike });
        this.commands.push({ id: 99 });
    }

    public AllHeartsNextInMainPlaylist() {
        this.appService.showNotification("sending All hearts Next in the main PL");
        this.commands.push({ id: 34 });
        this.commands.push({ id: 99 });
    }

    public AllHeartsNextInSpecificPlaylist(playlistID: number) {
        this.appService.showNotification("sending All hearts Next from a specific PL");
        this.commands.push({ id: 30, playlistID: playlistID });
        this.commands.push({ id: 99 });
    }

    public AllHeartsNextInTheSearchResults() {
        this.appService.showNotification("sending All hearts Next from a specific PL");
        this.commands.push({ id: 31 });
        this.commands.push({ id: 99 });
    }


}
